import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles,
  Loader2,
  Check,
  ArrowLeft,
  ArrowRight,
  Link2,
  Plus,
  X,
  CheckCircle2,
  Circle,
  FileText,
  Mic,
  Map as MapIcon,
} from "lucide-react";

import { OWLBERT_DARK } from "@/lib/owlbert";
const OWL_PORTRAIT = OWLBERT_DARK;

const STEPS = [
  { k: "identity", label: "Identity", sub: "who is this client", icon: FileText },
  { k: "voice", label: "Voice", sub: "how they write", icon: Mic },
  { k: "brain", label: "Brain", sub: "client + voice + strategy", icon: Sparkles },
  { k: "drafts", label: "First drafts", sub: "5 posts in their voice", icon: FileText },
  { k: "live", label: "Live", sub: "ready to ship", icon: MapIcon },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const params = useParams();
  const initialWsId = params.workspaceId || null;
  const [step, setStep] = useState(0);
  const [workspaceId, setWorkspaceId] = useState(initialWsId);
  const [status, setStatus] = useState(null);

  // Identity form
  const [form, setForm] = useState({
    name: "",
    company: "",
    product: "",
    linkedin_url: "",
    icp_description: "",
    voice_notes: "",
    product_features: [""],
    pain_points: [""],
  });
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, i, v) => setForm((f) => ({ ...f, [k]: f[k].map((x, j) => (i === j ? v : x)) }));
  const addItem = (k) => setForm((f) => ({ ...f, [k]: [...f[k], ""] }));
  const removeItem = (k, i) => setForm((f) => ({ ...f, [k]: f[k].filter((_, j) => j !== i) }));

  // Autofill state
  const [autofillUrl, setAutofillUrl] = useState("");
  const [autofilling, setAutofilling] = useState(false);
  const [autofillProfile, setAutofillProfile] = useState(null);
  const [autofillSamples, setAutofillSamples] = useState([]);
  const [savingIdentity, setSavingIdentity] = useState(false);

  // Voice samples step
  const [voiceSamples, setVoiceSamples] = useState([{ text: "", source: "manual" }]);
  const [savingSamples, setSavingSamples] = useState(false);
  const [defaultVoiceProfileId, setDefaultVoiceProfileId] = useState(null);

  // Brain generation state
  const [brainProgress, setBrainProgress] = useState({
    client: "idle",
    voice: "idle",
    strategy: "idle",
  });

  // Load existing workspace if resuming
  useEffect(() => {
    if (!workspaceId) return;
    (async () => {
      try {
        const r = await apiGet(`/operator/setup-status/${workspaceId}`);
        setStatus(r);
        // Decide which step to land on
        if (!r.steps.identity) setStep(0);
        else if (!r.steps.voice_samples) setStep(1);
        else if (!r.steps.client_doc || !r.steps.voice_doc || !r.steps.strategy_doc) setStep(2);
        else setStep(3);
        // Hydrate form from workspace
        const ws = await apiGet(`/workspaces/${workspaceId}`);
        setForm({
          name: ws.name || "",
          company: ws.company || "",
          product: ws.product || "",
          linkedin_url: ws.linkedin_url || "",
          icp_description: ws.icp_description || "",
          voice_notes: ws.voice_notes || "",
          product_features: ws.product_features?.length ? ws.product_features : [""],
          pain_points: ws.pain_points?.length ? ws.pain_points : [""],
        });
      } catch (e) {
        toast.error("could not load setup status");
      }
    })();
  }, [workspaceId]);

  const refreshStatus = async () => {
    if (!workspaceId) return;
    try {
      const r = await apiGet(`/operator/setup-status/${workspaceId}`);
      setStatus(r);
    } catch (e) {}
  };

  const runAutofill = async () => {
    if (!autofillUrl.trim()) {
      toast.error("paste a linkedin url");
      return;
    }
    setAutofilling(true);
    try {
      const r = await apiPost("/workspaces/autofill-from-linkedin", {
        linkedin_url: autofillUrl.trim(),
      });
      const d = r.draft || {};
      setForm({
        name: d.workspace_name || r.profile?.name || form.name,
        company: d.company || form.company,
        product: d.product || form.product,
        linkedin_url: r.profile?.linkedin_url || autofillUrl.trim(),
        icp_description: d.icp_description || form.icp_description,
        voice_notes: d.voice_notes || form.voice_notes,
        product_features: d.product_features?.length ? d.product_features : form.product_features,
        pain_points: d.pain_points?.length ? d.pain_points : form.pain_points,
      });
      setAutofillProfile(r.profile || {});
      setAutofillSamples(r.posts || []);
      // Pre-stage voice samples from scraped posts
      if (r.posts?.length) {
        setVoiceSamples(
          r.posts.slice(0, 5).map((p) => ({ text: p.text, source: "linkedin_post" }))
        );
      }
      toast.success("prefilled · review and adjust");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "autofill failed — fill manually");
    } finally {
      setAutofilling(false);
    }
  };

  const saveIdentity = async () => {
    if (!form.name.trim()) {
      toast.error("client name required");
      return;
    }
    setSavingIdentity(true);
    try {
      const payload = {
        ...form,
        product_features: form.product_features.filter((x) => x.trim()),
        pain_points: form.pain_points.filter((x) => x.trim()),
      };
      let ws;
      if (workspaceId) {
        const { apiPatch } = await import("@/lib/api");
        ws = await apiPatch(`/workspaces/${workspaceId}`, payload);
      } else {
        ws = await apiPost("/workspaces", payload);
        setWorkspaceId(ws.workspace_id);
      }
      // If we autofilled, also create a default voice profile so samples can attach
      if (autofillProfile?.linkedin_url && !defaultVoiceProfileId) {
        try {
          const vp = await apiPost(`/workspaces/${ws.workspace_id}/voice_profiles`, {
            name: "Personal Voice",
            linkedin_url: autofillProfile.linkedin_url,
            headline: autofillProfile.headline || "",
            voice_notes: form.voice_notes,
            avatar_url: autofillProfile.avatar_url || "",
            is_default: true,
            autofill: false,
          });
          setDefaultVoiceProfileId(vp.id);
        } catch (_) {}
      }
      toast.success("identity saved");
      setStep(1);
      refreshStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "save failed");
    } finally {
      setSavingIdentity(false);
    }
  };

  const saveVoiceSamples = async () => {
    if (!workspaceId) return;
    const valid = voiceSamples.filter((s) => s.text.trim().length > 30);
    if (valid.length < 3) {
      toast.error("at least 3 samples (30+ chars each) needed");
      return;
    }
    setSavingSamples(true);
    try {
      // Ensure a default voice profile exists
      let vpId = defaultVoiceProfileId;
      if (!vpId) {
        const existing = await apiGet(`/workspaces/${workspaceId}/voice_profiles`).catch(() => []);
        const def = existing?.find?.((v) => v.is_default) || existing?.[0];
        if (def) vpId = def.id;
        else {
          const vp = await apiPost(`/workspaces/${workspaceId}/voice_profiles`, {
            name: "Personal Voice",
            linkedin_url: form.linkedin_url || "",
            headline: "",
            voice_notes: form.voice_notes,
            is_default: true,
            autofill: false,
          });
          vpId = vp.id;
        }
        setDefaultVoiceProfileId(vpId);
      }
      for (const s of valid) {
        await apiPost(`/workspaces/${workspaceId}/voice_samples`, {
          sample_text: s.text.trim(),
          source: s.source || "manual",
          tone_tags: [],
          voice_profile_id: vpId,
        }).catch(() => {});
      }
      toast.success(`${valid.length} samples saved`);
      setStep(2);
      refreshStatus();
    } catch (e) {
      toast.error("save failed");
    } finally {
      setSavingSamples(false);
    }
  };

  const generateBrain = async () => {
    if (!workspaceId) return;
    setBrainProgress({ client: "running", voice: "idle", strategy: "idle" });
    try {
      await apiPost(`/workspaces/${workspaceId}/brain/client/generate`, { extra_notes: "" });
      setBrainProgress((p) => ({ ...p, client: "done", voice: "running" }));
      await apiPost(`/workspaces/${workspaceId}/brain/voice/extract`, {});
      setBrainProgress((p) => ({ ...p, voice: "done", strategy: "running" }));
      await apiPost(`/workspaces/${workspaceId}/brain/strategy/generate`, { use_top_posts: true });
      setBrainProgress((p) => ({ ...p, strategy: "done" }));
      toast.success("brain assembled");
      await refreshStatus();
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "generation failed — try again");
      setBrainProgress({ client: "idle", voice: "idle", strategy: "idle" });
    }
  };

  // First-drafts state
  const [draftsRunning, setDraftsRunning] = useState(false);
  const [generatedDrafts, setGeneratedDrafts] = useState([]);
  const [draftCount, setDraftCount] = useState(5);

  const runFirstDrafts = async () => {
    if (!workspaceId) return;
    setDraftsRunning(true);
    try {
      const r = await apiPost(`/workspaces/${workspaceId}/brain/first-drafts`, {
        count: draftCount,
      });
      setGeneratedDrafts(r.created || []);
      toast.success(`${r.count} ship-ready drafts pushed to pipeline`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "draft generation failed");
    } finally {
      setDraftsRunning(false);
    }
  };

  const progressDots = useMemo(() => {
    if (!status) return Array(5).fill(false);
    const s = status.steps;
    return [
      s.identity,
      s.voice_samples,
      s.client_doc && s.voice_doc && s.strategy_doc,
      generatedDrafts.length > 0,
      status.complete && generatedDrafts.length > 0,
    ];
  }, [status, generatedDrafts]);

  return (
    <Layout
      eyebrow="onboarding wizard"
      title={workspaceId ? `Setup · ${form.name || "client"}` : "Onboard a new client"}
      actions={
        <button
          onClick={() => navigate(workspaceId ? `/workspace/${workspaceId}` : "/dashboard")}
          className="btn-ghost inline-flex items-center gap-1.5"
          data-testid="wizard-exit"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> back
        </button>
      }
    >
      {/* Step navigator */}
      <div className="hairline px-8 py-5 mb-10 flex items-center justify-between gap-4 flex-wrap">
        {STEPS.map((s, i) => {
          const done = progressDots[i];
          const active = step === i;
          return (
            <button
              key={s.k}
              onClick={() => {
                if (workspaceId || i === 0) setStep(i);
              }}
              data-testid={`step-btn-${s.k}`}
              className="flex items-center gap-3 group disabled:cursor-not-allowed text-left"
              disabled={!workspaceId && i > 0}
            >
              <div
                className={`w-8 h-8 flex items-center justify-center border ${
                  active
                    ? "border-terracotta text-terracotta"
                    : done
                    ? "border-sage text-sage"
                    : "border-hairline text-stone-dim"
                } transition-colors`}
                style={done ? { color: "hsl(var(--accent))", borderColor: "hsl(var(--accent))" } : undefined}
              >
                {done ? <Check className="w-4 h-4" /> : <s.icon className="w-3.5 h-3.5" />}
              </div>
              <div>
                <div className="overline">step {i + 1}</div>
                <div className={`text-sm font-medium ${active ? "text-parchment" : "text-stone"}`}>
                  {s.label}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <span className="text-stone-dim mx-2 hidden md:inline">·</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Step content */}
      {step === 0 && (
        <StepIdentity
          form={form}
          setF={setF}
          setList={setList}
          addItem={addItem}
          removeItem={removeItem}
          autofillUrl={autofillUrl}
          setAutofillUrl={setAutofillUrl}
          autofilling={autofilling}
          runAutofill={runAutofill}
          autofillProfile={autofillProfile}
          autofillSamplesCount={autofillSamples.length}
          onSubmit={saveIdentity}
          submitting={savingIdentity}
        />
      )}
      {step === 1 && (
        <StepVoice
          samples={voiceSamples}
          setSamples={setVoiceSamples}
          onSubmit={saveVoiceSamples}
          submitting={savingSamples}
          onBack={() => setStep(0)}
          existingCount={status?.voice_samples_count || 0}
        />
      )}
      {step === 2 && (
        <StepBrain
          progress={brainProgress}
          onRun={generateBrain}
          onBack={() => setStep(1)}
          status={status}
        />
      )}
      {step === 3 && (
        <StepFirstDrafts
          drafts={generatedDrafts}
          running={draftsRunning}
          count={draftCount}
          setCount={setDraftCount}
          onRun={runFirstDrafts}
          onBack={() => setStep(2)}
          onNext={() => setStep(4)}
          workspaceId={workspaceId}
          navigate={navigate}
        />
      )}
      {step === 4 && (
        <StepLive
          workspaceId={workspaceId}
          name={form.name}
          navigate={navigate}
          draftCount={generatedDrafts.length}
        />
      )}
    </Layout>
  );
}

// ---------------------------------------------------------------------------
function StepIdentity({
  form,
  setF,
  setList,
  addItem,
  removeItem,
  autofillUrl,
  setAutofillUrl,
  autofilling,
  runAutofill,
  autofillProfile,
  autofillSamplesCount,
  onSubmit,
  submitting,
}) {
  return (
    <div className="max-w-3xl space-y-10 fade-up">
      {/* Quick-start autofill */}
      <section className="hairline px-8 py-7">
        <div className="overline mb-2">quick start · optional</div>
        <h2 className="serif text-2xl text-parchment mb-1">Pull from LinkedIn</h2>
        <p className="text-sm text-stone mb-5 max-w-xl">
          paste their profile url. we'll fetch headline + recent 5 posts and prefill the form.
          takes 30-60 seconds. uses apify + claude (~$0.10-0.30 per profile).
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[280px]">
            <Link2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
            <input
              type="text"
              value={autofillUrl}
              onChange={(e) => setAutofillUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !autofilling && runAutofill()}
              placeholder="https://linkedin.com/in/<handle>/"
              disabled={autofilling}
              data-testid="wizard-autofill-url"
              className="input-block pl-10"
            />
          </div>
          <button
            onClick={runAutofill}
            disabled={autofilling || !autofillUrl.trim()}
            data-testid="wizard-autofill-run"
            className="btn-primary inline-flex items-center gap-2"
          >
            {autofilling ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {autofilling ? "analyzing…" : "analyze & prefill"}
          </button>
        </div>
        {autofillProfile && (
          <div className="mt-5 flex items-center gap-3 px-4 py-3 hairline border-terracotta/30" data-testid="wizard-autofill-result">
            {autofillProfile.avatar_url && (
              <img
                src={autofillProfile.avatar_url}
                alt={autofillProfile.name}
                className="w-10 h-10 object-cover grayscale"
              />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-parchment font-medium truncate">
                {autofillProfile.name}
              </div>
              <div className="text-xs text-stone truncate">{autofillProfile.headline}</div>
            </div>
            <div className="mono text-[10px] uppercase tracking-wider text-terracotta">
              {autofillSamplesCount} samples queued
            </div>
          </div>
        )}
      </section>

      {/* Identity form */}
      <section className="space-y-8">
        <Group title="Identity">
          <Row>
            <Field label="Client name *">
              <input
                data-testid="wizard-name"
                value={form.name}
                onChange={(e) => setF("name", e.target.value)}
                placeholder="e.g. Angad / LeanSuite"
                className="input-block"
              />
            </Field>
            <Field label="Company">
              <input
                data-testid="wizard-company"
                value={form.company}
                onChange={(e) => setF("company", e.target.value)}
                className="input-block"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Product / Offer">
              <input
                data-testid="wizard-product"
                value={form.product}
                onChange={(e) => setF("product", e.target.value)}
                className="input-block"
              />
            </Field>
            <Field label="LinkedIn URL">
              <input
                data-testid="wizard-linkedin"
                value={form.linkedin_url}
                onChange={(e) => setF("linkedin_url", e.target.value)}
                className="input-block"
              />
            </Field>
          </Row>
        </Group>

        <Group title="ICP">
          <Field label="who is the target customer?">
            <textarea
              data-testid="wizard-icp"
              rows={4}
              value={form.icp_description}
              onChange={(e) => setF("icp_description", e.target.value)}
              placeholder="title, company size, industry, geography, pain points they have"
              className="input-block resize-y"
            />
          </Field>
        </Group>

        <Group title="Voice notes (optional shortcuts)">
          <Field label="frameworks, style rules, beliefs">
            <textarea
              data-testid="wizard-voice-notes"
              rows={5}
              value={form.voice_notes}
              onChange={(e) => setF("voice_notes", e.target.value)}
              placeholder="how do they sound? what beliefs do they repeat?"
              className="input-block resize-y"
            />
          </Field>
        </Group>

        <Group title="Product features">
          <ListEditor
            items={form.product_features}
            placeholder="e.g. searchable failure history"
            onChange={(i, v) => setList("product_features", i, v)}
            onAdd={() => addItem("product_features")}
            onRemove={(i) => removeItem("product_features", i)}
            testidPrefix="wizard-feature"
          />
        </Group>

        <Group title="Customer pain points">
          <ListEditor
            items={form.pain_points}
            placeholder="e.g. root cause analysis takes forever"
            onChange={(i, v) => setList("pain_points", i, v)}
            onAdd={() => addItem("pain_points")}
            onRemove={(i) => removeItem("pain_points", i)}
            testidPrefix="wizard-pain"
          />
        </Group>
      </section>

      <div className="pt-4 flex items-center justify-end gap-3">
        <button
          onClick={onSubmit}
          disabled={submitting}
          data-testid="wizard-identity-next"
          className="btn-primary inline-flex items-center gap-2"
        >
          {submitting ? "saving…" : "save & continue"}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StepVoice({ samples, setSamples, onSubmit, submitting, onBack, existingCount }) {
  const updateSample = (i, v) => setSamples((s) => s.map((x, j) => (j === i ? { ...x, text: v } : x)));
  const updateSource = (i, v) => setSamples((s) => s.map((x, j) => (j === i ? { ...x, source: v } : x)));
  const addSample = () => setSamples((s) => [...s, { text: "", source: "manual" }]);
  const removeSample = (i) => setSamples((s) => s.filter((_, j) => j !== i));
  const validCount = samples.filter((s) => s.text.trim().length > 30).length;

  return (
    <div className="max-w-3xl space-y-8 fade-up">
      <section className="hairline px-8 py-7">
        <div className="overline mb-2">step 2</div>
        <h2 className="serif text-3xl text-parchment mb-2">Voice samples</h2>
        <p className="text-sm text-stone mb-5 max-w-xl">
          paste 5-10 of their best writing — linkedin posts, newsletters, podcast transcripts, dms.
          the voice extractor needs raw material. minimum 3 samples, each at least 30 characters.
        </p>
        <div className="flex items-center gap-4 text-sm">
          <span className="mono text-stone">
            {validCount} valid / {samples.length} total
          </span>
          {existingCount > 0 && (
            <span className="mono text-[11px] uppercase tracking-wider text-stone-dim">
              + {existingCount} existing in workspace
            </span>
          )}
        </div>
      </section>

      <div className="space-y-4">
        {samples.map((s, i) => (
          <div
            key={i}
            className="hairline px-5 py-4 space-y-3"
            data-testid={`wizard-sample-${i}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="overline">sample {i + 1}</div>
              <div className="flex items-center gap-3">
                <select
                  value={s.source}
                  onChange={(e) => updateSource(i, e.target.value)}
                  className="bg-graphite-2 border border-hairline text-stone text-xs px-2 py-1 mono uppercase tracking-wider"
                  data-testid={`wizard-sample-source-${i}`}
                >
                  <option value="manual">manual</option>
                  <option value="linkedin_post">linkedin post</option>
                  <option value="newsletter">newsletter</option>
                  <option value="podcast">podcast / transcript</option>
                  <option value="dm">dm</option>
                  <option value="other">other</option>
                </select>
                {samples.length > 1 && (
                  <button
                    onClick={() => removeSample(i)}
                    className="text-stone-dim hover:text-terracotta"
                    data-testid={`wizard-sample-remove-${i}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <textarea
              value={s.text}
              onChange={(e) => updateSample(i, e.target.value)}
              placeholder="paste the actual text… the rawer the better. don't clean it up."
              rows={5}
              data-testid={`wizard-sample-text-${i}`}
              className="input-block resize-y"
            />
            <div className="mono text-[10px] text-stone-dim">
              {s.text.length} chars · {s.text.split(/\s+/).filter(Boolean).length} words
            </div>
          </div>
        ))}
        <button
          onClick={addSample}
          className="btn-ghost inline-flex items-center gap-2"
          data-testid="wizard-add-sample"
        >
          <Plus className="w-3.5 h-3.5" /> add sample
        </button>
      </div>

      <div className="pt-4 flex items-center justify-between gap-3">
        <button onClick={onBack} className="btn-ghost" data-testid="wizard-voice-back">
          back
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting || validCount < 3}
          data-testid="wizard-voice-next"
          className="btn-primary inline-flex items-center gap-2"
        >
          {submitting ? "saving…" : `save ${validCount} samples & continue`}
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StepBrain({ progress, onRun, onBack, status }) {
  const isRunning = Object.values(progress).some((s) => s === "running");
  const allIdle = Object.values(progress).every((s) => s === "idle");
  const alreadyHave = {
    client: !!status?.brain?.client,
    voice: !!status?.brain?.voice,
    strategy: !!status?.brain?.strategy,
  };

  const StateRow = ({ k, label, sub, state, exists }) => {
    let icon = <Circle className="w-4 h-4 text-stone-dim" strokeWidth={1.5} />;
    let color = "text-stone";
    if (state === "running") {
      icon = <Loader2 className="w-4 h-4 animate-spin" style={{ color: "hsl(var(--accent))" }} />;
      color = "text-parchment";
    } else if (state === "done" || exists) {
      icon = <CheckCircle2 className="w-4 h-4" style={{ color: "hsl(var(--accent))" }} strokeWidth={1.5} />;
      color = "text-parchment";
    }
    return (
      <div className="flex items-center gap-4 px-6 py-4 hairline" data-testid={`wizard-brain-${k}`}>
        {icon}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${color}`}>{label}</div>
          <div className="text-xs text-stone mt-0.5">{sub}</div>
        </div>
        <div className="mono text-[10px] uppercase tracking-wider text-stone-dim">
          {state === "running" ? "writing…" : exists ? "done" : state === "done" ? "fresh" : "pending"}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-3xl space-y-8 fade-up">
      <section className="hairline px-8 py-7">
        <div className="overline mb-2">step 3</div>
        <h2 className="serif text-3xl text-parchment mb-2">Assemble the brain</h2>
        <p className="text-sm text-stone mb-5 max-w-xl">
          three claude calls in sequence. CLIENT.md from workspace data, then VOICE.md from your
          samples + any scraped posts, then STRATEGY.md from both. takes 60-90 seconds total.
        </p>
        <p className="owlbert-voice" style={{ fontSize: 15 }}>
          this is the part everyone else skips. every draft you ever ship from this client will
          load these three documents first.
        </p>
      </section>

      <div className="space-y-3">
        <StateRow
          k="client"
          label="CLIENT.md"
          sub="who they are, what they sell, who they sell to"
          state={progress.client}
          exists={alreadyHave.client}
        />
        <StateRow
          k="voice"
          label="VOICE.md"
          sub="sentence patterns, vocabulary, beliefs, anti-patterns"
          state={progress.voice}
          exists={alreadyHave.voice}
        />
        <StateRow
          k="strategy"
          label="STRATEGY.md"
          sub="pillars, post-type rotation, CTA rotation, DM funnels"
          state={progress.strategy}
          exists={alreadyHave.strategy}
        />
      </div>

      <div className="pt-4 flex items-center justify-between gap-3">
        <button onClick={onBack} className="btn-ghost" data-testid="wizard-brain-back" disabled={isRunning}>
          back
        </button>
        <button
          onClick={onRun}
          disabled={isRunning}
          data-testid="wizard-brain-run"
          className="btn-primary inline-flex items-center gap-2"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {isRunning
            ? "thinking…"
            : allIdle
            ? "run brain generation"
            : "re-run"}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StepFirstDrafts({ drafts, running, count, setCount, onRun, onBack, onNext, workspaceId, navigate }) {
  const hasDrafts = drafts && drafts.length > 0;
  return (
    <div className="max-w-3xl space-y-8 fade-up">
      <section className="hairline px-8 py-7">
        <div className="overline mb-2">step 4 · optional but recommended</div>
        <h2 className="serif text-3xl text-parchment mb-2">First drafts</h2>
        <p className="text-sm text-stone mb-5 max-w-xl">
          one claude call. reads CLIENT.md + VOICE.md + STRATEGY.md. produces {count} ship-ready
          drafts that spread across the strategy's pillars. each one lands in the content pipeline
          as DRAFT — review, tweak, ship.
        </p>
        <p className="owlbert-voice" style={{ fontSize: 15 }}>
          the discovery sprint at most agencies takes 10 hours of strategy work to produce 3-5
          drafts. we just compressed it into 60 seconds.
        </p>
      </section>

      {!hasDrafts ? (
        <div className="hairline px-8 py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <label className="overline">how many</label>
            <select
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value, 10))}
              disabled={running}
              data-testid="wizard-drafts-count"
              className="bg-graphite-2 border border-hairline text-parchment px-3 py-2 mono text-sm"
            >
              {[3, 4, 5, 6, 7].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={onRun}
            disabled={running}
            data-testid="wizard-drafts-run"
            className="btn-primary inline-flex items-center gap-2"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {running ? "drafting…" : `produce ${count} drafts`}
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="wizard-drafts-list">
          {drafts.map((d, i) => (
            <div key={d.post_id} className="hairline px-6 py-5" data-testid={`wizard-draft-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="mono text-[10px] uppercase tracking-wider text-stone-dim">
                  draft {i + 1}
                </span>
                {d.pillar && (
                  <span className="tag-chip" data-tag="learning">
                    {d.pillar}
                  </span>
                )}
                {d.format && d.format !== "text" && (
                  <span className="tag-chip" data-tag="voice">
                    {d.format}
                  </span>
                )}
              </div>
              <div className="serif text-xl text-parchment leading-snug mb-2">{d.title}</div>
              {d.hook && (
                <p className="text-sm text-stone leading-relaxed mb-2 line-clamp-2">
                  <span className="text-parchment">hook: </span>
                  {d.hook}
                </p>
              )}
              {d.why_it_works && (
                <p className="owlbert-voice mt-3" style={{ fontSize: 14 }}>
                  {d.why_it_works}
                </p>
              )}
              <div className="mt-3">
                <button
                  onClick={() => navigate(`/workspace/${workspaceId}/posts`)}
                  className="btn-text inline-flex items-center gap-1"
                  data-testid={`wizard-draft-open-${i}`}
                >
                  open in pipeline <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pt-4 flex items-center justify-between gap-3">
        <button onClick={onBack} className="btn-ghost" data-testid="wizard-drafts-back" disabled={running}>
          back
        </button>
        <div className="flex items-center gap-3">
          {hasDrafts && (
            <button
              onClick={onRun}
              disabled={running}
              className="btn-ghost inline-flex items-center gap-2"
              data-testid="wizard-drafts-rerun"
            >
              <Sparkles className="w-3 h-3" /> re-run
            </button>
          )}
          <button
            onClick={onNext}
            data-testid="wizard-drafts-next"
            className="btn-primary inline-flex items-center gap-2"
          >
            {hasDrafts ? "finish setup" : "skip & finish"} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StepLive({ workspaceId, name, navigate, draftCount = 0 }) {
  return (
    <div className="max-w-2xl mx-auto text-center fade-up py-12">
      <img src={OWL_PORTRAIT} alt="Owlbert" className="owlbert-portrait w-24 h-24 mx-auto mb-8" />
      <div className="overline mb-3">live</div>
      <h2 className="serif text-5xl text-parchment mb-4 leading-tight">
        {name} is ready.
      </h2>
      <p className="text-base text-stone max-w-md mx-auto mb-3 leading-relaxed">
        the brain is assembled. every draft, comment, and strategy nudge from here on loads
        their CLIENT.md, VOICE.md, and STRATEGY.md before generating.
      </p>
      {draftCount > 0 && (
        <p className="owlbert-voice max-w-md mx-auto mb-10" style={{ fontSize: 15 }}>
          {draftCount} ship-ready drafts are waiting in the pipeline.
        </p>
      )}
      <div className="flex items-center justify-center gap-3 flex-wrap">
        <button
          onClick={() => navigate(`/workspace/${workspaceId}/posts`)}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="wizard-live-pipeline"
        >
          open the pipeline <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => navigate(`/workspace/${workspaceId}/brain`)}
          className="btn-ghost inline-flex items-center gap-2"
          data-testid="wizard-live-brain"
        >
          open the brain
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className="btn-text"
          data-testid="wizard-live-dashboard"
        >
          back to command center
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
function Group({ title, children }) {
  return (
    <div>
      <div className="overline mb-3">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Row({ children }) {
  return <div className="grid md:grid-cols-2 gap-5">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-stone mb-1.5">{label}</label>
      {children}
    </div>
  );
}
function ListEditor({ items, onChange, onAdd, onRemove, placeholder, testidPrefix }) {
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <input
            value={v}
            onChange={(e) => onChange(i, e.target.value)}
            placeholder={placeholder}
            data-testid={`${testidPrefix}-${i}`}
            className="input-block flex-1"
          />
          {items.length > 1 && (
            <button
              onClick={() => onRemove(i)}
              className="btn-ghost px-3"
              data-testid={`${testidPrefix}-remove-${i}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      ))}
      <button
        onClick={onAdd}
        className="btn-text inline-flex items-center gap-1.5"
        data-testid={`${testidPrefix}-add`}
      >
        <Plus className="w-3 h-3" /> add
      </button>
    </div>
  );
}
